import React, { useState } from 'react';
import { T } from '../shared/constants';
import { ItemFrame, Instruction, CheckButton, ResultCard, partColors, usePointerDrag, DragGhost } from './ItemChrome';
import { buildStudentAnswer, isCorrectResponse, feedbackFor, partMarks, seededShuffle } from './naplanItems';

const FONT = "'Plus Jakarta Sans', sans-serif";

/** `match` — sort each item into a category. Response: category index per item. */
export default function MatchRenderer({ question, studentAnswer, state, showExplanation, onSubmit, onAskLumi, onExplainVideo }) {
  const isAnswered = studentAnswer != null;
  const items = question.items || [];
  const categories = question.categories || [];
  const [draft, setDraft] = useState(() => items.map(() => null));
  const [picked, setPicked] = useState(null); // item index
  const [poolOrder] = useState(() => seededShuffle(items.map((_, i) => i), question.id));
  const placement = isAnswered && Array.isArray(studentAnswer.rawAnswer) ? studentAnswer.rawAnswer : draft;
  const isCorrect = isAnswered ? (typeof studentAnswer.isCorrect === 'boolean' ? studentAnswer.isCorrect : isCorrectResponse(question, placement)) : false;
  const marks = isAnswered ? partMarks(question, placement) : [];
  const allPlaced = items.length > 0 && draft.every(c => c !== null && c !== undefined);

  function place(itemIdx, cat) {
    setDraft(d => { const n = [...d]; n[itemIdx] = cat; return n; });
    setPicked(null);
  }

  const { drag, start, consumeClick } = usePointerDrag({
    disabled: isAnswered,
    onDrop: (itemIdx, key) => {
      if (key === 'pool') place(itemIdx, null);
      else if (key.startsWith('cat:')) place(itemIdx, Number(key.slice(4)));
    },
  });

  function tapChip(i) {
    if (isAnswered || consumeClick(i)) return;
    setPicked(p => (p === i ? null : i));
  }

  function tapTarget(cat) {
    if (isAnswered || picked === null) return;
    place(picked, cat);
  }

  function submit() {
    if (isAnswered || !allPlaced) return;
    onSubmit(buildStudentAnswer(question, [...draft]));
  }

  function chip(i) {
    const it = items[i];
    let status = picked === i ? 'selected' : null;
    if (isAnswered) status = marks[i] ? 'right' : 'wrong';
    const c = partColors(status);
    const where = placement[i] === null || placement[i] === undefined ? 'not placed' : `in ${categories[placement[i]]}`;
    return (
      <button
        key={i}
        type="button"
        data-testid={`match-item-${i}`}
        data-status={status || 'none'}
        aria-pressed={!isAnswered ? picked === i : undefined}
        aria-label={isAnswered ? `${it.text}, ${where}, ${marks[i] ? 'correct' : 'incorrect'}` : `${it.text}, ${where}${picked === i ? ', picked up' : ''}`}
        disabled={isAnswered}
        onClick={() => tapChip(i)}
        onPointerDown={e => start(i, it.text, e)}
        style={{
          background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 10, padding: "8px 12px",
          fontSize: 14, fontWeight: status ? 700 : 500, color: c.color, fontFamily: FONT,
          cursor: isAnswered ? "default" : "grab", touchAction: "none", userSelect: "none",
          opacity: drag && drag.id === i ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        {isAnswered && <span aria-hidden="true">{marks[i] ? '✓' : '✗'}</span>}
        {it.text}
      </button>
    );
  }

  const pool = poolOrder.filter(i => placement[i] === null || placement[i] === undefined);
  const zoneStyle = key => {
    const over = drag && drag.over === key;
    return {
      background: over ? T.accentLo : T.surface,
      border: `1.5px ${over || picked !== null ? 'solid' : 'dashed'} ${over ? T.accent : picked !== null ? T.borderHi : T.border}`,
      borderRadius: 12, padding: 12, minHeight: 64, transition: "all .15s",
    };
  };
  const targetBtn = (label, onClick, aria) => (
    <button
      type="button" onClick={onClick} aria-label={aria} disabled={isAnswered || picked === null}
      style={{
        background: "none", border: "none", padding: 0, margin: "0 0 8px", fontSize: 13, fontWeight: 800,
        color: picked !== null && !isAnswered ? T.accent : T.text, cursor: picked !== null && !isAnswered ? "pointer" : "default",
        fontFamily: FONT, textAlign: "left", display: "block", width: "100%",
      }}
    >{label}{picked !== null && !isAnswered && <span style={{ fontWeight: 600, color: T.textMid }}> — tap to place here</span>}</button>
  );

  return (
    <ItemFrame question={question} state={state} isAnswered={isAnswered}>
      <Instruction>Sort each item into the right box</Instruction>
      {!isAnswered && (
        <p style={{ fontSize: 13, color: T.textMid, margin: "0 0 10px" }}>
          Drag an item into a box, or tap an item and then tap a box.
        </p>
      )}
      {!isAnswered && (
        <div data-drop-key="pool" data-testid="match-pool" role="group" aria-label="Items to sort" style={{ ...zoneStyle('pool'), marginBottom: 14 }}>
          {targetBtn('Items to sort', () => tapTarget(null), picked !== null ? `Put "${items[picked].text}" back with the items to sort` : 'Items to sort')}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {pool.length ? pool.map(chip) : <span style={{ fontSize: 13, color: T.textDim }}>All items placed.</span>}
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`, gap: 12, marginBottom: 20 }}>
        {categories.map((cat, ci) => (
          <div key={ci} data-drop-key={`cat:${ci}`} data-testid={`match-cat-${ci}`} role="group" aria-label={cat} style={zoneStyle(`cat:${ci}`)}>
            {targetBtn(cat, () => tapTarget(ci), picked !== null ? `Put "${items[picked].text}" in ${cat}` : cat)}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {items.map((_, i) => i).filter(i => placement[i] === ci).map(chip)}
            </div>
          </div>
        ))}
      </div>
      <DragGhost drag={drag} />
      {!isAnswered && <CheckButton mode={state.mode} disabled={!allPlaced} onClick={submit} />}
      <ResultCard
        question={question} mode={state.mode} isAnswered={isAnswered} isCorrect={isCorrect}
        showExplanation={showExplanation} feedback={isAnswered && !isCorrect ? feedbackFor(question, placement) : null}
        onAskLumi={onAskLumi} onExplainVideo={onExplainVideo}
      />
    </ItemFrame>
  );
}
